import { useMemo, useState } from "react";
import { MAX_ANNOUNCEMENT_LENGTH } from "../../../hooks/useAdminAnnouncements";
import { readsAs } from "../../../lib/announcements";
import type { AdminOpenClaw } from "../../../types/openclaw";
import own from "./Announcements.module.css";

type Props = {
  claws: ReadonlyArray<AdminOpenClaw>;
  /** Resolves true when the draft was created, which clears the form. */
  onCreate: (body: string, tenantIds: ReadonlyArray<string> | null) => Promise<boolean>;
};

export const AnnouncementComposer = ({ claws, onCreate }: Props): JSX.Element => {
  const [body, setBody] = useState<string>("");
  const [everyone, setEveryone] = useState<boolean>(true);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [busy, setBusy] = useState<boolean>(false);

  const length = body.trim().length;
  const tooLong = length > MAX_ANNOUNCEMENT_LENGTH;
  const canCreate = length > 0 && !tooLong && !busy && (everyone || selected.size > 0);

  const submit = async (): Promise<void> => {
    setBusy(true);
    const created = await onCreate(body.trim(), everyone ? null : [...selected]);
    setBusy(false);
    if (!created) return;
    setBody("");
    setSelected(new Set());
  };

  return (
    <section className={own.composer} data-testid="announcement-composer">
      <label className={own.label} htmlFor="announcement-body">
        Message
      </label>
      <textarea
        id="announcement-body"
        className={own.textarea}
        value={body}
        onChange={(event) => setBody(event.target.value)}
        placeholder="Plain text. Telegram, WhatsApp, Slack and Teams each format Markdown differently, so it is sent as written."
        data-testid="announcement-body"
      />
      <div className={`${own.counter} ${tooLong ? own.counterOver : ""}`}>
        {length} / {MAX_ANNOUNCEMENT_LENGTH}
      </div>
      {length > 0 && <ReadsAs body={body} />}
      <Audience everyone={everyone} selectedCount={selected.size} onChange={setEveryone} />
      {!everyone && <TenantPicker claws={claws} selected={selected} onChange={setSelected} />}
      <div className={own.actions}>
        <button
          type="button"
          className={own.primaryButton}
          disabled={!canCreate}
          onClick={() => void submit()}
          data-testid="announcement-create"
        >
          {busy ? "Working..." : "Create draft (dry run)"}
        </button>
        <span className={own.hint}>Checks where it would land on each instance. Nothing is sent until you press Send on the draft.</span>
      </div>
    </section>
  );
};

const ReadsAs = ({ body }: { body: string }): JSX.Element => (
  <div>
    <div className={own.label}>Customers will read</div>
    <div className={own.readsAs} data-testid="announcement-reads-as">
      {readsAs(body)}
    </div>
  </div>
);

const Audience = ({
  everyone,
  selectedCount,
  onChange,
}: {
  everyone: boolean;
  selectedCount: number;
  onChange: (everyone: boolean) => void;
}): JSX.Element => (
  <div className={own.audience} role="radiogroup" aria-label="Audience">
    <label>
      <input type="radio" name="audience" checked={everyone} onChange={() => onChange(true)} /> Every instance
    </label>
    <label>
      <input
        type="radio"
        name="audience"
        checked={!everyone}
        onChange={() => onChange(false)}
        data-testid="announcement-audience-selected"
      />{" "}
      Chosen instances {!everyone && `(${selectedCount})`}
    </label>
  </div>
);

const TenantPicker = ({
  claws,
  selected,
  onChange,
}: {
  claws: ReadonlyArray<AdminOpenClaw>;
  selected: ReadonlySet<string>;
  onChange: (next: ReadonlySet<string>) => void;
}): JSX.Element => {
  const sorted = useMemo(() => [...claws].sort((a, b) => a.name.localeCompare(b.name)), [claws]);
  const toggle = (tenantId: string): void => {
    const next = new Set(selected);
    if (next.has(tenantId)) next.delete(tenantId);
    else next.add(tenantId);
    onChange(next);
  };
  return (
    <div className={own.tenantPicker} data-testid="announcement-tenant-picker">
      {sorted.map((claw) => (
        <label key={claw.tenantId}>
          <input type="checkbox" checked={selected.has(claw.tenantId)} onChange={() => toggle(claw.tenantId)} />{" "}
          {claw.name} <span className={own.secondary}>({claw.tenantId})</span>
        </label>
      ))}
    </div>
  );
};
